import { inject, Injectable } from '@angular/core';
import { User } from 'firebase/auth';
import { AlertController, LoadingController, NavController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AuthService, ProviderSignInResult } from './auth.service';
import { ProfileService } from './profile.service';
import { UserProfile, hasOwnDisplayName } from '../classes/UserProfile';
import { SongDraftIntent, SongDraftService } from './song-draft.service';
import { BackendService } from './backend.service';
import {
  NEWSLETTER_CONSENT_TEXT,
  NEWSLETTER_CONSENT_VERSION,
  NEWSLETTER_REWARD_PERCENT,
} from '../config/newsletter-consent';
import { CONTACT_MAIL } from '../config/legal';
import { ToastService } from './toast.service';
import { AnalyticsService } from './analytics.service';

export enum NavigationIntent {
  BuyPreviewSong = "buy_preview_song"
}

@Injectable({
  providedIn: 'root'
})
export class AuthFlowService {
  private authService = inject(AuthService);
  private profileService = inject(ProfileService);
  private songDraftService = inject(SongDraftService);
  backendService = inject(BackendService);
  private analyticsService = inject(AnalyticsService);

  private flowRunning = false;

  /**
   * Wohin es nach der Anmeldung gehen soll, wenn es nicht die Startseite ist.
   *
   * Im sessionStorage und nicht im Dienst: der Anbieter-Login laeuft je nach
   * Browser ueber einen Redirect, und dann ist die App zwischendurch neu
   * geladen. Dieselbe Ueberlegung wie bei `login-intent` darueber.
   */
  private static readonly RETURN_ROUTE_KEY = 'melowish_after_login_route';

  /**
   * Merker, dass nach der Anbieter-Anmeldung nach dem Newsletter zu fragen ist.
   *
   * **Warum gefragt und nicht am Haekchen abgelesen.** Das Haekchen steht im
   * Formular ueber "Account erstellen" - zwei Reihen ueber den Anbieter-
   * Knoepfen. Niemand setzt es dort und klickt dann unten auf Google; der
   * Zusammenhang ist nicht zu sehen. Auf diesem Weg wird deshalb gefragt.
   *
   * **Warum danach und nicht davor.** Ein Dialog vor `signInWithPopup` haette
   * das Anbieter-Fenster hinter ein `await` geschoben, und damit zaehlt der
   * Klick fuer den Popup-Blocker nicht mehr als Nutzeraktion - dieselbe Falle
   * wie seinerzeit beim Anhang der Bewertung. Danach ist ausserdem bekannt, ob
   * das Konto die Frage schon beantwortet hat.
   */
  private static readonly NEWSLETTER_ASK_KEY = 'melowish_newsletter_ask';

  static rememberToAskForNewsletter(): void {
    try {
      sessionStorage.setItem(AuthFlowService.NEWSLETTER_ASK_KEY, '1');
    } catch {
      // Blockierter Storage - dann bleibt das Angebot im Profil.
    }
  }

  private takeNewsletterAsk(): boolean {
    try {
      const ask = sessionStorage.getItem(AuthFlowService.NEWSLETTER_ASK_KEY);
      sessionStorage.removeItem(AuthFlowService.NEWSLETTER_ASK_KEY);
      return ask === '1';
    } catch {
      return false;
    }
  }

  static rememberReturnRoute(route: string[]): void {
    try {
      sessionStorage.setItem(AuthFlowService.RETURN_ROUTE_KEY, route.join('/'));
    } catch {
      // Blockierter Storage - dann geht es eben zur Startseite.
    }
  }

  private takeReturnRoute(): string | null {
    try {
      const route = sessionStorage.getItem(AuthFlowService.RETURN_ROUTE_KEY);
      sessionStorage.removeItem(AuthFlowService.RETURN_ROUTE_KEY);
      return route;
    } catch {
      return null;
    }
  }

  /** Laeuft ein Umhaengen, warten beide Wege auf dasselbe Promise. */
  private mergePromise: Promise<void> | null = null;

  constructor(
    private navCtrl: NavController,
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
  ) {}

  /**
   * Wird einmal beim App Start aufgerufen
   */
  init() {
    this.authService.authState$
      .subscribe(user => this.handleAuthState(user));
  }

  /**
   * Login Buttons rufen nur das auf
   */
  async signInGoogle() {
    await this.runProviderSignIn(
      () => this.authService.signInGoogle(),
      'Google'
    );
  }

  async signInApple() {
    await this.runProviderSignIn(
      () => this.authService.signInApple(),
      'Apple'
    );
  }

  /**
   * Gemeinsamer Ablauf fuer Google und Apple.
   *
   * Neu ist der Schritt `settleAnonymousMerge`: kam der Nutzer aus einer
   * anonymen Sitzung und gehoerte die Identitaet schon einem Konto, ist er
   * jetzt an seinem bestehenden Konto angemeldet - und sein anonym erzeugter
   * Song haengt noch am alten. Der muss umgehaengt sein, **bevor** der
   * Login-Flow weiterlaeuft, sonst schaut der Nutzer im schlimmsten Fall auf
   * eine leere Songliste.
   */
  private async runProviderSignIn(
    signIn: () => Promise<ProviderSignInResult>,
    label: string
  ) {
    let loading: HTMLIonLoadingElement | null = null;

    try {
      sessionStorage.setItem('login-intent', '1');

      const result = await signIn();

      // Ab hier wird es fuer den Nutzer still: das Anbieter-Fenster ist zu,
      // aber Umhaengen und Profilabgleich laufen noch, beides ueber Callables.
      // Bei kaltem Backend sind das zwei bis drei Sekunden, in denen die Seite
      // unveraendert dasteht - und die Anmeldung gescheitert aussieht.
      //
      // Bewusst erst jetzt: waehrend des Popups haette eine Anzeige im
      // Hintergrund niemand gesehen.
      loading = await this.loadingCtrl.create({
        message: 'Anmeldung läuft...',
        cssClass: 'customLoading',
      });
      await loading.present();

      // Muss vor afterLogin stehen: der Nutzer soll nicht auf einer leeren
      // Songliste landen, waehrend sein Song noch umgehaengt wird.
      await this.settleAnonymousMerge();

      // WEB FLOW
      if (result.user) {
        await this.afterLogin(result.user);
      }
      // NATIVE FLOW kommt später über authState
    } catch (e: any) {
      console.error(`${label} Login fehlgeschlagen:`, e);
      sessionStorage.removeItem('login-intent');
      await loading?.dismiss();
      loading = null;
      alert(this.mapAuthError(e));
    } finally {
      await loading?.dismiss();
    }
  }

  /**
   * Stellt sicher, dass ein anstehendes Umhaengen abgeschlossen ist.
   *
   * Zwei Wege fuehren hierher und beide koennen zuerst ankommen: der Aufruf,
   * der die Anmeldung ausgeloest hat, und die authState-Reaktion. Wer zuerst
   * kommt, startet die Arbeit und legt das Promise ab; der zweite wartet auf
   * dasselbe. Ohne das koennte der Login-Flow anlaufen, waehrend der Song noch
   * am alten Konto haengt - und der Nutzer schaut im Moment der Conversion auf
   * eine leere Liste.
   *
   * Scheitert bewusst leise: der Nutzer ist angemeldet, und ihn deswegen aus
   * dem Ablauf zu werfen macht es nicht besser. Der Song ist nicht verloren, er
   * haengt am anonymen Konto und laesst sich per Support nachtragen.
   */
  private async settleAnonymousMerge(): Promise<void> {
    const token = this.authService.takePendingMergeToken();

    if (token) {
      this.mergePromise = (async () => {
        try {
          const result = await this.backendService.claimAnonymousAccount(token);
          console.log('Anonymer Besitz umgehaengt:', result.movedByCollection);
          this.trackMergedGuest(result.anonUserId);
        } catch (e) {
          console.error('Umhaengen des anonymen Besitzes fehlgeschlagen', e);
        }
      })();
    }

    if (this.mergePromise) {
      await this.mergePromise;
    }
  }

  /**
   * Hält fest, dass aus einem Gastkonto ein **bestehendes** Konto geworden ist.
   *
   * Ohne das blieb diese Umwandlung unsichtbar. Die Karte "Konten" paart
   * anonymes und echtes Signup über dieselbe Kennung: wer sein Gastkonto
   * verknüpft, behält sie, und `convertAnonUser` meldet das Paar. Wer dagegen
   * schon ein Konto hatte, wird dort angemeldet - andere Kennung, und
   * `convertAnonUser` steigt bei `CONVERT_MERGED` vorher aus. Das Gastkonto sah
   * damit aus wie eines, aus dem nichts wurde, und die Quote lag zu niedrig,
   * genau um die Wiederkehrer.
   *
   * **Die Kennung ist die anonyme, nicht die aktuelle.** Nur so entsteht das
   * Paar mit dem Gastkonto-Ereignis. Der Server liefert sie in `anonUserId` -
   * er hat das Token entschlüsselt, das ist die belastbare Quelle.
   *
   * Hier und nicht in `convertAnonUser`: beide Wege laufen durch das Umhängen,
   * der Passwort-Weg wie der über Google und Apple. In `convertAnonUser` hätte
   * es nur den ersten erwischt.
   *
   * Eigener Anmeldeweg `anon_merge`, weil es keine neue Registrierung ist - das
   * Konto gab es schon. Das Dashboard zählt es deshalb in die Umwandlung, aber
   * nicht in die Zahl der Registrierungen.
   */
  private trackMergedGuest(anonUserId: string): void {
    // Gleiche Kennung heisst: das Verknüpfen hat geklappt, es wurde nichts
    // umgehängt. Dann meldet `convertAnonUser` die Umwandlung.
    if (!anonUserId || anonUserId === this.authService.userId) return;

    void this.analyticsService.trackOnce(`${anonUserId}:anon_merge`, 'signup', {
      userId: anonUserId,
      method: 'anon_merge',
    });
  }

  /**
   * Wird aufgerufen wenn Firebase Auth State sich ändert.
   *
   * **Anonyme Anmeldungen bleiben draußen.** Seit 2.1b entsteht beim Absenden
   * des Formulars still ein anonymes Konto - mitten in einem fremden Ablauf,
   * mit offenem Ladebalken. Dieser Flow ist dafür nicht gedacht: er endete in
   * `dismissLoading()`, und das schliesst den obersten Balken, egal wer ihn
   * geöffnet hat. Der Balken war also weg, bevor der Song überhaupt angefordert
   * war - sichtbar als ein Formular, das nach dem Absenden einfach dasteht.
   *
   * Für ein anonymes Konto bleibt dabei nichts liegen. `ensureProfile` läuft
   * ohnehin zweimal woanders (beim Anlegen der Sitzung und im
   * `AppComponent`-Abo), `completeRegistration` und der Namensdialog hängen
   * beide an `!user.isAnonymous`, und ohne `login-intent` - das setzen nur die
   * Anbieter-Buttons - steigt der Rest des Flows ohnehin vorzeitig aus. Das
   * Umhängen ist auch nicht betroffen: ein Merge-Token entsteht erst beim
   * Verknüpfen, und danach ist der Nutzer nicht mehr anonym.
   */
  private async handleAuthState(user: User | null) {
    if (!user || user.isAnonymous || this.flowRunning) return;

    this.flowRunning = true;
    try {
      // Redirect-Weg: das Umhaengen kann sein Ergebnis nicht an den
      // ausloesenden Aufruf zurueckgeben, die App wurde zwischenzeitlich neu
      // geladen. Deshalb liegt das Token am AuthService bereit.
      await this.settleAnonymousMerge();
      await this.afterLogin(user);
    } finally {
      this.flowRunning = false;
    }
  }

  /**
   * Zentraler Login Flow
   */
  private async afterLogin(user: User) {

    let profile = await this.profileService.ensureProfile(user);

    // Adresse und Name ans Profil, falls sie dort fehlen.
    //
    // `ensureProfile` legt sie nur beim ANLEGEN des Profils ab. Meldet sich
    // jemand aus einer anonymen Sitzung ueber Google oder Apple an, wird das
    // Konto verknuepft - die Kennung bleibt, das Profil existiert schon, und
    // die Funktion steigt vorne aus. Die Adresse fehlte danach dauerhaft:
    // `isGuest()` meldete weiter "Gastkonto", und die Songseite bot die
    // Registrierung an, die gerade stattgefunden hatte.
    //
    // Serverseitig, weil die Firestore-Regel dem Client am Profil nur
    // displayName und photoURL erlaubt - derselbe Weg wie bei der
    // Registrierung per Mail.
    if (!user.isAnonymous && profile && !profile.email) {
      try {
        await this.backendService.completeRegistration({
          displayName: user.displayName ?? undefined,
        });
        profile = await this.profileService.ensureProfile(user);
      } catch (e) {
        console.error('Profil konnte nicht nachgetragen werden', e);
      }
    }

    // Der Ladebalken gehoert weg, bevor ein Dialog kommt.
    //
    // Sonst liegen zwei Overlays uebereinander: der Namensdialog war hinter
    // dem Balken nicht erreichbar, und weil der Aufrufer auf einen gesetzten
    // Namen wartet, drehte sich der Balken endlos.
    await this.dismissLoading();

    // Anonyme Konten bekommen keinen Namensdialog. Seit 2.1b entsteht so ein
    // Konto still beim Absenden des Formulars - ein Dialog waere genau die
    // Reibung, die dort gerade abgebaut wurde. Gefragt wird bei der
    // Umwandlung in ein echtes Konto.
    // "Gast" zaehlt dabei nicht als Name: seit anonyme Konten den Platzhalter
    // tragen, wuerde eine blosse Pruefung auf "leer" den Dialog verschlucken -
    // der Nutzer hiesse nach der Registrierung fuer immer Gast.
    if (profile && !hasOwnDisplayName(profile.displayName) && !user.isAnonymous) {
      await this.askForDisplayName();
    }

    const loginIntent = sessionStorage.getItem('login-intent');
    if (!loginIntent) return;
    sessionStorage.removeItem('login-intent');

    if (this.takeNewsletterAsk()) {
      await this.offerNewsletter(profile);
    }

    await this.goAfterAuth();
  }

  /** Schliesst einen noch offenen Ladebalken, egal wer ihn geoeffnet hat. */
  private async dismissLoading(): Promise<void> {
    try {
      const top = await this.loadingCtrl.getTop();
      await top?.dismiss();
    } catch {
      // Kein Balken offen - dann gibt es nichts zu schliessen.
    }
  }

  /**
   * Fragt nach der Anbieter-Anmeldung einmalig nach dem Newsletter.
   *
   * Nur, wenn das Konto die Frage noch nie beantwortet hat - ob gerade neu
   * angelegt oder schon vorhanden, spielt dabei keine Rolle. Der Wortlaut
   * steht im Dialog selbst und nicht hinter einem "Mehr": ein Alert hat keinen
   * Platz fuer einen zweiten Weg, und informiert sein muss die Einwilligung
   * trotzdem.
   *
   * Die Adresse ist bei Google und Apple vom Anbieter geprueft. Das Backend
   * sieht das am Token und bestaetigt sofort - der Rabattcode liegt also
   * gleich im Profil, ohne Mail dazwischen.
   */
  private async offerNewsletter(profile: UserProfile | null): Promise<void> {
    if (!profile || profile.isGuest()) return;
    if (profile.newsletterConsent?.requestedAt) return;

    const alert = await this.alertCtrl.create({
      header: 'Newsletter?',
      message:
        `${NEWSLETTER_CONSENT_TEXT}\n\n` +
        `Jederzeit widerrufbar — eine formlose E-Mail an ${CONTACT_MAIL} genügt.`,
      cssClass: 'infoAlert',
      buttons: [
        { text: 'Nein danke', role: 'cancel' },
        { text: 'Ja, gerne', role: 'yes' },
      ],
    });

    await alert.present();

    // onWillDismiss: das Ende der Abblendung kommt in dieser App erst rund
    // anderthalb Sekunden spaeter - nachgemessen, siehe askForNotifyEmail in
    // song-create.
    const { role } = await alert.onWillDismiss();

    if (role !== 'yes') return;

    // Mit Anzeige: dahinter stecken ein Aufruf ins Backend und einer zu
    // Stripe, bei kaltem Backend zusammen gut drei Sekunden. Ohne etwas
    // Sichtbares wirkt der Knopf, als haette er nicht getroffen.
    const loading = await this.loadingCtrl.create({
      message: 'Wird angemeldet...',
      cssClass: 'customLoading',
    });
    await loading.present();

    try {
      const { confirmed, rewardCode } =
        await this.backendService.requestNewsletterConsent(NEWSLETTER_CONSENT_VERSION);

      if (confirmed && rewardCode) {
        ToastService.makeToast(
          `Angemeldet — dein Rabattcode über ${NEWSLETTER_REWARD_PERCENT} % liegt in deinem Profil.`
        );
      }
    } catch (e) {
      console.error('Newsletter-Anmeldung fehlgeschlagen', e);
    } finally {
      await loading.dismiss();
    }
  }

  /**
   * Wohin es nach einer Anmeldung oder Registrierung geht.
   *
   * Oeffentlich und an einer Stelle, weil es zwei Wege hierher gibt: der
   * Anbieter-Login laeuft ueber `afterLogin`, die Registrierung per Mail
   * navigiert aus der Signup-Seite heraus. Die lief bisher immer auf die
   * Startseite - der gemerkte Song griff nur bei Google und Apple.
   */
  async goAfterAuth(): Promise<void> {
    if (this.songDraftService.hasDraft()) {
      await this.createPreviewSongFromDraft();
      return;
    }

    // Wer sich aus einem Song heraus angemeldet hat, will zurueck zu diesem
    // Song - nicht auf die Startseite. `justUnlocked` sagt der Seite, dass sie
    // nach oben scrollen und es erwaehnen soll.
    const returnRoute = this.takeReturnRoute();

    if (returnRoute) {
      await this.navCtrl.navigateRoot([returnRoute], {
        state: { justUnlocked: true },
      });
      return;
    }

    await this.navCtrl.navigateRoot(['home']);
  }

  async createPreviewSongFromDraft() {
    const draftPayload = this.songDraftService.getDraft();
    if(draftPayload) {
      draftPayload.tier = "preview";

      const loading = await this.loadingCtrl.create({
        message: 'Song wird zugeordnet...',
        cssClass: "customLoading"
      });

      loading.present();

      try {
        const resp = await this.backendService.createNewSong(draftPayload);
        console.log('Song request created:', resp);

        if(this.songDraftService.hasDraft()) {
          this.songDraftService.clearDraft();
        }

        // Erst navigieren, dann den Hinweis wegnehmen - sonst steht der
        // Nutzer ein paar hundert Millisekunden vor der alten Seite und
        // haelt es fuer einen Fehlschlag.
        await this.navCtrl.navigateRoot(["song-list"]);
        await loading.dismiss();

      } catch (e: any) {
        console.error('Error creating song from form', e);
        loading.dismiss();
        await this.backendService.processBackendError(e);
      }
    }
  }

  /**
   * Wartet auf Profil Update
   */
  private async askForDisplayName() {
    const named = await this.profileService.askForDisplayName();

    // Abgebrochen: dann gibt es nichts, worauf zu warten waere. Vorher lief
    // der Flow hier in eine Warteschleife, die nie endete - und mit ihr blieb
    // die Anmeldung unvollendet.
    if (!named) return;

    // Warten, bis der Name wirklich im Profil steht. Der Schreibvorgang
    // laeuft gegen Firestore, die Anzeige haengt am Snapshot.
    await firstValueFrom(
      this.profileService.profile$.pipe(
        filter((p): p is UserProfile => !!p && hasOwnDisplayName(p.displayName))
      )
    );
  }

  mapAuthError(e: any): string {
      const code = e?.code ?? '';
      switch (code) {
        case 'auth/invalid-email':
          return 'Die E-Mail-Adresse ist ungültig.';
        case 'auth/user-disabled':
          return 'Dieses Konto wurde deaktiviert.';
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          return 'E-Mail oder Passwort ist falsch.';
        case 'auth/too-many-requests':
          return 'Zu viele Versuche. Bitte später erneut.';
        case 'auth/popup-closed-by-user':
          return 'Anmeldung abgebrochen.';
        default:
          return 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.';
      }
    }
}
